import { type BillingMethod } from '@/features/billing/profile/BillingMethod'
import { ModalViewModel } from '@/infra/ModalViewModel'
import { Job } from '@/features/jobs/Job'
import { action, computed, observable } from 'mobx'
import { type Observable } from 'rxjs'
import { fromStream } from '@/utils/mobx-util'
import { links } from '@/utils/link'
import { Id } from '@/infra/id'
import type { AlertErrorHandlerFactory } from '@/features/shell'
import type { NavigateFn } from '@/RootStore'

/**
 * Submission status view model.
 */
export class SubmissionStatusModalViewModel {
  readonly modal = new ModalViewModel()

  /**
   * Submission status.
   */
  @observable accessor status: SubmissionStatus = 'Submitting'

  /**
   * If `status` is `Failed`, represents what went wrong.
   */
  @observable accessor error = ''

  /**
   * Streams as Observables.
   *
   * @private
   */
  private readonly _$

  /**
   * Constructor.
   */
  constructor(
    private readonly _job$: Observable<Job>,
    private readonly _paymentMethod: () => BillingMethod | null,
    private readonly _copyJob: () => Promise<{ jobId: Id }>,
    private readonly _canBypassBilling: () => boolean,
    private readonly _navigate: NavigateFn,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
  ) {
    this._$ = {
      job: fromStream(Job.none, this._job$),
    }
  }

  /**
   * The payment method that was used, if any.
   */
  @computed
  get paymentMethod() {
    return this._paymentMethod()
  }

  /**
   * Whether billing can be bypassed.
   */
  @computed
  get canBypassBilling() {
    return this._canBypassBilling()
  }

  /**
   * The job that was submitted.
   */
  @computed
  get job() {
    return this._$.job()
  }

  /**
   * Whether the submission succeeded.
   */
  @computed
  get isSuccess() {
    return this.status === 'Success'
  }

  /**
   * The submitted job's name, once the submission succeeded.
   */
  @computed
  get jobDisplayName() {
    return this.isSuccess ? this.job.displayName : ''
  }

  /**
   * Link to the submitted job, once the submission succeeded.
   */
  @computed
  get continueTo() {
    return this.isSuccess ? links.job(Id.assert(this.job.id)) : undefined
  }

  /**
   * Copies the submitted job and opens the copy.
   */
  @action.bound
  async copyJob() {
    const { jobId } = await this._copyJob().catch(
      this._createAlertErrorHandler('Copying the job failed'),
    )
    this._navigate(links.job(Id.assert(jobId)))
  }

  /**
   * Shows the modal in the submitting state.
   */
  @action.bound
  startSubmission() {
    this.status = 'Submitting'
    this.error = ''
    this.modal.show()
  }

  /**
   * The submission failed.
   * @param error
   */
  @action.bound
  submissionFailed(error: string) {
    this.status = 'Failed'
    this.error = error
  }

  /**
   * The submission was successful.
   */
  @action.bound
  submissionSuccessful() {
    this.status = 'Success'
    this.error = ''
  }
}

/**
 * Submission status.
 */
export type SubmissionStatus = 'Submitting' | 'Success' | 'Failed'
